import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DialogSelect } from "@/components/ui/dialog-select";
import { Product } from "@/types/product";
import { Customer } from "@/types/customer";
import { Plus, Loader2, Trash2, ShoppingCart, Check, Pencil, ChevronDown, ChevronUp } from "lucide-react";
import { useToast } from "@/contexts/toast-context";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { DialogDatePicker } from "@/components/ui/dialog-date-picker";

interface SaleItem {
  id: string;
  deviceName: string;
  deviceDescription: string;
  salePrice: string;
  monthlyInstallment: string;
  currency: "" | "IQD" | "USD";
  date: Date;
  notes: string;
  isConfirmed: boolean;
}

interface MultiSaleDialogProps {
  customers: Customer[];
  products: Product[];
  onAdd: (product: Omit<Product, "id" | "createdAt" | "remainingAmount">) => Promise<void> | void;
}

let itemCounter = 0;

const createEmptyItem = (): SaleItem => {
  const now = new Date();
  // Use 12:00 noon to avoid timezone issues that could shift the date by a day
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 12, 0, 0, 0);
  return {
    id: `item-${Date.now()}-${++itemCounter}`,
    deviceName: "",
    deviceDescription: "",
    salePrice: "",
    monthlyInstallment: "",
    currency: "",
    date,
    notes: "",
    isConfirmed: false,
  };
};

export function MultiSaleDialog({ customers, products, onAdd }: MultiSaleDialogProps) {
  const { error: showError, success } = useToast();
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [items, setItems] = useState<SaleItem[]>([]);
  const [newItemId, setNewItemId] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const inputRefs = useRef<Map<string, HTMLInputElement>>(new Map());

  const toggleExpanded = (id: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };


  const customerOptions = customers.map(customer => {
    const customerProds = products.filter(p => p.customerId === customer.id);
    const active = customerProds.filter(p => p.remainingAmount > 0).length;
    const completed = customerProds.filter(p => p.remainingAmount === 0).length;

    let statsText = '';
    if (customerProds.length > 0) {
      const parts = [];
      if (active > 0) parts.push(`${active} قیست ماوە`);
      if (completed > 0) parts.push(`${completed} تەواوبوو`);
      statsText = parts.join(' • ');
    }

    return {
      value: customer.id,
      label: `${customer.fullName} - ${customer.customerCode}`,
      description: `${customer.phone1}${customer.isBlocked ? ' • بلۆک کراو' : statsText ? ` • ${statsText}` : ''}`,
      disabled: customer.isBlocked,
    };
  });

  const currencyOptions = [
    { value: "IQD", label: "دینار" },
    { value: "USD", label: "دۆلار" },
  ];

  // فۆکەس بکە سەر input ی نوێ
  useEffect(() => {
    if (newItemId) {
      // چاوەڕوانی بکە تاوەکو DOM نوێ ببێتەوە
      setTimeout(() => {
        const input = inputRefs.current.get(newItemId);
        if (input) {
          input.focus();
          input.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        // لە مۆبایل expand بکە
        setExpandedItems(prev => new Set(prev).add(newItemId));
        setNewItemId(null);
      }, 50);
    }
  }, [newItemId, items]);

  const handleCustomerChange = (id: string) => {
    const customer = customers.find(c => c.id === id);
    if (customer?.isBlocked) {
      showError(`کڕیار "${customer.fullName}" بلۆک کراوە`);
      return;
    }
    setCustomerId(id);
  };

  const updateItem = (id: string, field: keyof SaleItem, value: string | Date) => {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, [field]: value } : item
    ));
  };

  const isItemValid = (item: SaleItem) => {
    return item.deviceName && item.salePrice && item.monthlyInstallment && item.currency;
  };

  // پشکنین ئایا دەکرێت لاینی نوێ زیاد بکرێت
  const canAddNewItem = items.length === 0 || items.every(item => item.isConfirmed);

  const addItem = () => {
    if (!canAddNewItem) {
      showError("تکایە سەرەتا کاڵای پێشوو تۆمار بکە");
      return;
    }
    const newItem = createEmptyItem();
    setItems(prev => [...prev, newItem]);
    setNewItemId(newItem.id);
  };

  const confirmItem = (id: string) => {
    const item = items.find(i => i.id === id);
    if (item && isItemValid(item)) {
      const salePrice = parseFloat(item.salePrice);
      const monthlyInstallment = parseFloat(item.monthlyInstallment);

      // چێککردنی ئەوەی قیستی مانگانە لە نرخی فرۆشتن زیاتر نەبێت
      if (monthlyInstallment > salePrice) {
        showError("قیستی مانگانە ناتوانێت لە نرخی فرۆشتن زیاتر بێت!");
        return;
      }

      setItems(prev => prev.map(i =>
        i.id === id ? { ...i, isConfirmed: true } : i
      ));
    }
  };

  const editItem = (id: string) => {
    setItems(prev => prev.map(i =>
      i.id === id ? { ...i, isConfirmed: false } : i
    ));
  };

  const removeItem = (id: string) => {
    setItems(prev => prev.filter(item => item.id !== id));
  };

  // پشکنین ئایا لاینەیەکی ناتەواو هەیە کە نووسینی تێدایە
  const hasUnconfirmedItemWithData = (item: SaleItem) => {
    return !item.isConfirmed && (item.deviceName || item.salePrice || item.monthlyInstallment || item.currency);
  };

  const canSubmit = customerId && items.length > 0 && items.every(item => item.isConfirmed);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // پشکنین بۆ لاینە ناتەواوەکان
    const unconfirmedWithData = items.filter(hasUnconfirmedItemWithData);
    if (unconfirmedWithData.length > 0) {
      showError(`${unconfirmedWithData.length} کاڵا هەیە کە تۆمار نەکراوە. تکایە تۆماریان بکە یان بیانسڕەوە.`);
      return;
    }

    if (!canSubmit || isSubmitting) return;

    // چێککردنی ئەوەی قیستی مانگانە لە نرخی فرۆشتن زیاتر نەبێت
    for (const item of items) {
      const salePrice = parseFloat(item.salePrice);
      const monthlyInstallment = parseFloat(item.monthlyInstallment);
      if (monthlyInstallment > salePrice) {
        showError(`قیستی مانگانە بۆ "${item.deviceName}" ناتوانێت لە نرخی فرۆشتن زیاتر بێت!`);
        return;
      }
    }

    setIsSubmitting(true);
    try {
      for (const item of items) {
        await onAdd({
          customerId,
          deviceName: item.deviceName,
          deviceDescription: item.deviceDescription,
          salePrice: parseFloat(item.salePrice),
          monthlyInstallment: parseFloat(item.monthlyInstallment),
          installments: 0,
          currency: item.currency as "IQD" | "USD",
          paidAmount: 0,
          date: (() => {
            const d = item.date;
            const year = d.getFullYear();
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
          })(),
          notes: item.notes,
        });
      }

      success(`${items.length} فرۆشتن بە سەرکەوتوویی زیادکران`);
      setCustomerId("");
      setItems([]);
      setOpen(false);
    } catch {
      showError("هەڵەیەک ڕوویدا لە زیادکردنی فرۆشتنەکان");
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedCustomer = customers.find(c => c.id === customerId);

  // حیسابکردنی کاڵاکانی کڕیار
  const customerProducts = customerId ? products.filter(p => p.customerId === customerId) : [];
  const activeProducts = customerProducts.filter(p => p.remainingAmount > 0).length;
  const completedProducts = customerProducts.filter(p => p.remainingAmount <= 0.001).length;

  // کۆی گشتی
  const totalIQD = items
    .filter(i => i.currency === "IQD")
    .reduce((sum, i) => sum + (parseFloat(i.salePrice) || 0), 0);
  const totalUSD = items
    .filter(i => i.currency === "USD")
    .reduce((sum, i) => sum + (parseFloat(i.salePrice) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" className="flex items-center gap-2 text-xs md:text-sm px-3 py-2 md:px-4 md:py-2">
          <Plus className="h-3 w-3 md:h-4 md:w-4" />
          <span className="hidden sm:inline">فرۆشتنی نوێ</span>
          <span className="sm:hidden">فرۆشتن</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="w-[98vw] !max-w-7xl h-[95vh] md:h-[95vh] flex flex-col p-0 sm:!max-w-7xl">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            فرۆشتنی نوێ
          </DialogTitle>
          <DialogDescription>
            کڕیار هەڵبژێرە و کاڵاکان زیاد بکە
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          {/* هەڵبژاردنی کڕیار */}
          <div className="px-6 py-4 border-b bg-muted/30 shrink-0">
            <div className="flex flex-col md:flex-row md:items-end gap-4">
              <div className="flex-1 space-y-2">
                <Label className="font-semibold">هەڵبژاردنی کڕیار *</Label>
                <DialogSelect
                  value={customerId}
                  onChange={handleCustomerChange}
                  options={customerOptions}
                  placeholder="کڕیار هەڵبژێرە"
                  searchable={true}
                  searchPlaceholder="گەڕان بە ناو، ژمارە، یان کۆد..."
                  emptyMessage="هیچ کڕیارێک نەدۆزرایەوە"
                />
              </div>
              {selectedCustomer && (
                <div className="px-4 py-2.5 rounded-lg bg-background border">
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{selectedCustomer.fullName}</span>
                      <span className="text-muted-foreground text-xs">•</span>
                      <span className="text-muted-foreground text-xs">{selectedCustomer.phone1}</span>
                    </div>
                    {customerProducts.length > 0 && (
                      <div className="flex items-center gap-2 text-xs">
                        {activeProducts > 0 && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400">
                            <span className="font-semibold">{activeProducts}</span>
                            <span>قیست ماوە</span>
                          </span>
                        )}
                        {completedProducts > 0 && (
                          <span className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-green-500/10 text-green-700 dark:text-green-400">
                            <span className="font-semibold">{completedProducts}</span>
                            <span>تەواوبوو</span>
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* خشتەی کاڵاکان */}
          <div className="flex-1 overflow-auto px-4 md:px-6 py-4">
            <div className="flex items-center justify-between mb-4">
              <Label className="font-semibold text-base">کاڵاکان ({items.length})</Label>
              <Button
                type="button"
                onClick={addItem}
                disabled={!canAddNewItem}
                className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground shadow-md hover:shadow-lg transition-all"
              >
                <Plus className="h-5 w-5" />
                <span className="hidden sm:inline">زیادکردنی کاڵا</span>
                <span className="sm:hidden">زیادکردن</span>
              </Button>
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead className="text-right w-10">#</TableHead>
                    <TableHead className="text-right min-w-[150px]">ناوی ئامێر *</TableHead>
                    <TableHead className="text-right min-w-[120px]">وەسف</TableHead>
                    <TableHead className="text-right w-[100px]">دراو *</TableHead>
                    <TableHead className="text-right w-[120px]">نرخ *</TableHead>
                    <TableHead className="text-right w-[120px]">قیستی مانگانە *</TableHead>
                    <TableHead className="text-right w-[130px]">بەروار *</TableHead>
                    <TableHead className="text-right min-w-[100px]">تێبینی</TableHead>
                    <TableHead className="text-center w-[100px]">کردارەکان</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                        هیچ کاڵایەک زیاد نەکراوە، کلیک لە "زیادکردنی کاڵا" بکە
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((item, index) => (
                      <TableRow key={item.id} className={`hover:bg-muted/30 ${item.isConfirmed ? 'bg-green-50 dark:bg-green-950/20' : !isItemValid(item) ? 'bg-amber-50 dark:bg-amber-950/20' : ''}`}>
                        <TableCell className="font-medium text-center">
                          <span className={`h-6 w-6 rounded-full inline-flex items-center justify-center text-xs ${item.isConfirmed ? 'bg-green-500/20 text-green-600' : isItemValid(item) ? 'bg-primary/10 text-primary' : 'bg-amber-500/20 text-amber-600'}`}>
                            {item.isConfirmed ? <Check className="h-3 w-3" /> : index + 1}
                          </span>
                        </TableCell>
                        <TableCell>
                          <Input
                            ref={(el) => {
                              if (el) inputRefs.current.set(item.id, el);
                            }}
                            value={item.deviceName}
                            onChange={(e) => updateItem(item.id, "deviceName", e.target.value)}
                            placeholder="ناوی ئامێر"
                            className="h-9"
                            disabled={item.isConfirmed}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.deviceDescription}
                            onChange={(e) => updateItem(item.id, "deviceDescription", e.target.value)}
                            placeholder="وەسف"
                            className="h-9"
                            disabled={item.isConfirmed}
                          />
                        </TableCell>
                        <TableCell>
                          <DialogSelect
                            value={item.currency}
                            onChange={(value) => updateItem(item.id, "currency", value)}
                            options={currencyOptions}
                            placeholder="دراو"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.salePrice}
                            onChange={(e) => updateItem(item.id, "salePrice", e.target.value)}
                            placeholder="0"
                            dir="ltr"
                            className="text-center h-9"
                            disabled={item.isConfirmed}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            value={item.monthlyInstallment}
                            onChange={(e) => updateItem(item.id, "monthlyInstallment", e.target.value)}
                            placeholder="0"
                            dir="ltr"
                            className="text-center h-9"
                            disabled={item.isConfirmed}
                          />
                        </TableCell>
                        <TableCell>
                          <DialogDatePicker
                            value={item.date}
                            onChange={(date) => updateItem(item.id, "date", date)}
                            disabled={item.isConfirmed}
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            value={item.notes}
                            onChange={(e) => updateItem(item.id, "notes", e.target.value)}
                            placeholder="تێبینی"
                            className="h-9"
                            disabled={item.isConfirmed}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1">
                            {item.isConfirmed ? (
                              <button
                                type="button"
                                onClick={() => editItem(item.id)}
                                className="h-7 w-7 rounded-md flex items-center justify-center bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700 transition-all shadow-sm"
                                title="دەستکاری"
                              >
                                <Pencil className="h-4 w-4" />
                              </button>
                            ) : (
                              <button
                                type="button"
                                onClick={() => confirmItem(item.id)}
                                disabled={!isItemValid(item)}
                                className={`h-7 w-7 rounded-md flex items-center justify-center transition-all ${isItemValid(item)
                                  ? 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700 shadow-sm'
                                  : 'bg-gray-200 text-gray-400 cursor-not-allowed dark:bg-gray-700 dark:text-gray-500'
                                  }`}
                                title="تۆمارکردن"
                              >
                                <Check className="h-4 w-4" />
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => removeItem(item.id)}
                              className="h-7 w-7 rounded-md flex items-center justify-center bg-red-500 text-white hover:bg-red-600 active:bg-red-700 transition-all shadow-sm"
                              title="سڕینەوە"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {items.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground border rounded-lg">
                  هیچ کاڵایەک زیاد نەکراوە، کلیک لە "زیادکردن" بکە
                </div>
              ) : (
                items.map((item, index) => (
                  <div
                    key={item.id}
                    className={`border rounded-lg overflow-hidden ${item.isConfirmed ? 'border-green-500/50 bg-green-50 dark:bg-green-950/20' : !isItemValid(item) ? 'border-amber-500/50 bg-amber-50 dark:bg-amber-950/20' : ''}`}
                  >
                    {/* Card Header */}
                    <div
                      className="flex items-center justify-between p-3 bg-muted/30 cursor-pointer"
                      onClick={() => toggleExpanded(item.id)}
                    >
                      <div className="flex items-center gap-2">
                        <span className={`h-6 w-6 rounded-full inline-flex items-center justify-center text-xs ${item.isConfirmed ? 'bg-green-500/20 text-green-600' : isItemValid(item) ? 'bg-primary/10 text-primary' : 'bg-amber-500/20 text-amber-600'}`}>
                          {item.isConfirmed ? <Check className="h-3 w-3" /> : index + 1}
                        </span>
                        <span className="font-medium text-sm">
                          {item.deviceName || 'کاڵای نوێ'}
                        </span>
                        {item.salePrice && (
                          <span className="text-xs text-muted-foreground">
                            - {parseFloat(item.salePrice).toLocaleString()} {item.currency}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Action buttons in header */}
                        <div className="flex items-center gap-1">
                          {item.isConfirmed ? (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); editItem(item.id); }}
                              className="h-7 w-7 rounded-md flex items-center justify-center bg-blue-500 text-white"
                            >
                              <Pencil className="h-4 w-4" />
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); confirmItem(item.id); }}
                              disabled={!isItemValid(item)}
                              className={`h-7 w-7 rounded-md flex items-center justify-center ${isItemValid(item) ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                                }`}
                            >
                              <Check className="h-4 w-4" />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); removeItem(item.id); }}
                            className="h-7 w-7 rounded-md flex items-center justify-center bg-red-500 text-white"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                        {expandedItems.has(item.id) ? (
                          <ChevronUp className="h-4 w-4 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </div>

                    {/* Card Content */}
                    {expandedItems.has(item.id) && (
                      <div className="p-3 space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                          <div className="col-span-2">
                            <Label className="text-xs text-muted-foreground">ناوی ئامێر *</Label>
                            <Input
                              ref={(el) => {
                                if (el) inputRefs.current.set(item.id, el);
                              }}
                              value={item.deviceName}
                              onChange={(e) => updateItem(item.id, "deviceName", e.target.value)}
                              placeholder="ناوی ئامێر"
                              className="h-9 mt-1"
                              disabled={item.isConfirmed}
                            />
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs text-muted-foreground">وەسف</Label>
                            <Input
                              value={item.deviceDescription}
                              onChange={(e) => updateItem(item.id, "deviceDescription", e.target.value)}
                              placeholder="وەسف"
                              className="h-9 mt-1"
                              disabled={item.isConfirmed}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">دراو *</Label>
                            <div className="mt-1">
                              <DialogSelect
                                value={item.currency}
                                onChange={(value) => updateItem(item.id, "currency", value)}
                                options={currencyOptions}
                                placeholder="دراو"
                              />
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">نرخ *</Label>
                            <Input
                              type="number"
                              value={item.salePrice}
                              onChange={(e) => updateItem(item.id, "salePrice", e.target.value)}
                              placeholder="0"
                              dir="ltr"
                              className="text-center h-9 mt-1"
                              disabled={item.isConfirmed}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">قیستی مانگانە *</Label>
                            <Input
                              type="number"
                              value={item.monthlyInstallment}
                              onChange={(e) => updateItem(item.id, "monthlyInstallment", e.target.value)}
                              placeholder="0"
                              dir="ltr"
                              className="text-center h-9 mt-1"
                              disabled={item.isConfirmed}
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-muted-foreground">بەروار *</Label>
                            <div className="mt-1">
                              <DialogDatePicker
                                value={item.date}
                                onChange={(date) => updateItem(item.id, "date", date)}
                                disabled={item.isConfirmed}
                              />
                            </div>
                          </div>
                          <div className="col-span-2">
                            <Label className="text-xs text-muted-foreground">تێبینی</Label>
                            <Input
                              value={item.notes}
                              onChange={(e) => updateItem(item.id, "notes", e.target.value)}
                              placeholder="تێبینی"
                              className="h-9 mt-1"
                              disabled={item.isConfirmed}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          {/* کۆی گشتی و دوگمەکان */}
          <div className="px-6 py-4 border-t bg-muted/30 shrink-0">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              {/* کۆی گشتی */}
              <div className="flex flex-wrap gap-3">
                {totalIQD > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border">
                    <span className="text-muted-foreground text-sm">کۆی دینار:</span>
                    <span className="font-bold text-lg">{totalIQD.toLocaleString()}</span>
                    <span className="text-xs text-muted-foreground">IQD</span>
                  </div>
                )}
                {totalUSD > 0 && (
                  <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-background border">
                    <span className="text-muted-foreground text-sm">کۆی دۆلار:</span>
                    <span className="font-bold text-lg">${totalUSD.toLocaleString()}</span>
                  </div>
                )}
                {totalIQD === 0 && totalUSD === 0 && (
                  <div className="text-sm text-muted-foreground">
                    هیچ کاڵایەک زیاد نەکراوە
                  </div>
                )}
              </div>

              {/* دوگمەکان */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setOpen(false)}
                  disabled={isSubmitting}
                  className="min-w-[100px]"
                >
                  پاشگەزبوونەوە
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="min-w-[140px]"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin ml-2" />
                      چاوەڕوان بە...
                    </>
                  ) : (
                    `فرۆشتنی ${items.length} کاڵا`
                  )}
                </Button>
              </div>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
